import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { ComplianceDocument, ComplianceDocumentCreate, DocumentType, InductionAttemptResult, InductionToTake, LocalDate, PermitAccept, PrearrivalPermits, RequirementMode } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { SignaturePad, UploadWithProgress } from '../../components/phase3';
import type { SignatureValue } from '../../components/phase3';
import { Alert, Badge, Button, TextField } from '../../components/ui';
import { InductionTaker } from '../contactless-public/InductionTaker';
import type { AttemptInput } from '../contactless-public/InductionTaker';
import { problemText } from './api';
import { Section } from './PublicShell';

/**
 * Phase 3 sections of the pre-arrival page (docs/modules/compliance.md section 6, permits.md section 3): documents to upload,
 * the induction (video and quiz) and permits to accept. Each section asks its own module for the visitor's link; a module
 * that is off, or has nothing for this visitor, answers an error or an empty list and the section is simply not drawn.
 * Same credential as the rest of the page: the signed pre-arrival link in the path, no session.
 */

const enc = encodeURIComponent;
const quiet = { quiet401: true } as const;

// ---------- Documents ----------

/**
 * GET /compliance/prearrival/:token. The compliance server (S28) is not merged yet and the shared package has no response shape
 * for this route, so this is the page's reading of the contract ("requirements for the visitor's member record, if any"):
 * one row per required document type with its standing today.
 */
export const PrearrivalDocuments = z.object({
  items: z.array(
    DocumentType.pick({ id: true, name: true, description: true, needsIssueDate: true, needsExpiry: true, needsFile: true }).extend({
      mode: RequirementMode,
      state: z.enum(['valid', 'expiring', 'expired', 'missing', 'pending_review', 'rejected']),
      expiryDate: LocalDate.nullable(),
    }),
  ),
});
export type PrearrivalDocuments = z.infer<typeof PrearrivalDocuments>;
type DocItem = PrearrivalDocuments['items'][number];

const MAX_FILE = 20 * 1024 * 1024;
const todo = (s: DocItem['state']): boolean => s === 'missing' || s === 'expired' || s === 'rejected' || s === 'expiring';

function DocumentCard({ token, item, onSaved }: { token: string; item: DocItem; onSaved: () => void }) {
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [issue, setIssue] = useState('');
  const [expiry, setExpiry] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => {
      const body = ComplianceDocumentCreate.parse({ typeId: item.id, uploadId, reference, issueDate: issue || null, expiryDate: expiry || null });
      return api.post(`/compliance/prearrival/${enc(token)}/documents`, { body, schema: ComplianceDocument, ...quiet });
    },
    onSuccess: onSaved,
    onError: (e) => setProblem(problemText(e)),
  });
  const submit = () => {
    if (item.needsFile && !uploadId) return setProblem(t('w21.docs.needFile'));
    if (item.needsExpiry && !expiry) return setProblem(t('w21.docs.needExpiry'));
    if (item.needsIssueDate && !issue) return setProblem(t('w21.docs.needIssue'));
    setProblem(null);
    save.mutate();
  };
  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-medium">{item.name}</h3>
        <Badge tone={item.state === 'valid' || item.state === 'pending_review' ? 'good' : item.mode === 'refuse' ? 'bad' : 'warn'}>{t(`w21.docs.state.${item.state}`)}</Badge>
      </div>
      {item.description ? <p className="text-sm text-slate-700">{item.description}</p> : null}
      {item.state === 'rejected' ? <p className="text-sm text-red-800">{t('w21.docs.rejectedHelp')}</p> : null}
      {todo(item.state) ? (
        <>
          {item.needsFile ? (
            <UploadWithProgress
              label={t('w21.docs.file')}
              hint={t('w21.docs.fileHint')}
              url={`/api/v1/compliance/prearrival/${enc(token)}/uploads`}
              maxBytes={MAX_FILE}
              accept={['application/pdf', 'image/*']}
              onDone={(r) => setUploadId(z.object({ uploadId: z.string() }).safeParse(r).data?.uploadId ?? null)}
              onError={setProblem}
            />
          ) : null}
          <TextField label={t('w21.docs.reference')} value={reference} maxLength={120} onChange={(e) => setReference(e.target.value)} />
          {item.needsIssueDate ? <TextField label={t('w21.docs.issueDate')} type="date" value={issue} onChange={(e) => setIssue(e.target.value)} /> : null}
          {item.needsExpiry ? <TextField label={t('w21.docs.expiryDate')} type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} /> : null}
          {problem ? <Alert>{problem}</Alert> : null}
          <Button variant="primary" className="min-h-12 w-full" disabled={save.isPending} onClick={submit}>
            {t('w21.docs.save')}
          </Button>
        </>
      ) : (
        <p className="flex items-center gap-2 text-sm font-medium text-green-800">
          <Check aria-hidden="true" className="h-4 w-4" />
          {item.state === 'pending_review' ? t('w21.docs.waiting') : t('w21.docs.ok')}
        </p>
      )}
    </div>
  );
}

export function DocumentsSection({ token }: { token: string }) {
  const qc = useQueryClient();
  const key = ['public-link', 'prearrival', token, 'documents'];
  const q = useQuery({ queryKey: key, queryFn: () => api.get(`/compliance/prearrival/${enc(token)}`, { schema: PrearrivalDocuments, ...quiet }), retry: false });
  if (!q.data || q.data.items.length === 0) return null;
  return (
    <Section title={t('w21.docs.title')}>
      <p className="text-sm text-slate-700">{t('w21.docs.help')}</p>
      {q.data.items.map((i) => (
        <DocumentCard key={`${i.id}-${i.state}`} token={token} item={i} onSaved={() => void qc.invalidateQueries({ queryKey: key })} />
      ))}
    </Section>
  );
}

// ---------- Induction ----------

export function InductionSection({ token }: { token: string }) {
  const key = ['public-link', 'prearrival', token, 'inductions'];
  const q = useQuery({ queryKey: key, queryFn: () => api.get(`/compliance/prearrival/${enc(token)}/inductions`, { schema: z.array(InductionToTake), ...quiet }), retry: false });
  if (!q.data || q.data.length === 0) return null;
  const submit = (a: AttemptInput) => api.post(`/compliance/prearrival/${enc(token)}/inductions/attempts`, { body: a, schema: InductionAttemptResult, ...quiet });
  return (
    <Section title={t('w21.induction.title')}>
      <p className="text-sm text-slate-700">{t('w21.induction.help')}</p>
      <InductionTaker items={q.data} submit={submit} />
    </Section>
  );
}

// ---------- Permits ----------

type PermitsView = z.infer<typeof PrearrivalPermits>;
type PermitItem = PermitsView['permits'][number];

function PermitCard({ token, permit, onAccepted }: { token: string; permit: PermitItem; onAccepted: (p: PermitsView) => void }) {
  // The server needs drawn strokes for a permit (a typed name or a blank pad is refused), so there is no typed-name route here.
  const [sig, setSig] = useState<SignatureValue | null>(null);
  const [name, setName] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const accept = useMutation({
    mutationFn: () => {
      const body = PermitAccept.parse({ name: name.trim(), strokes: sig, occurredAt: new Date().toISOString(), stage: 'accept' });
      return api.post(`/permits/prearrival/${enc(token)}/${enc(permit.id)}/accept`, { body, schema: PrearrivalPermits, ...quiet });
    },
    onSuccess: onAccepted,
    onError: (e) => setProblem(problemText(e)),
  });
  const when = (iso: string | null) => (iso ? new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso)) : '');
  const canAccept = permit.status === 'issued' && !permit.accepted;
  const submit = () => {
    if (!sig) return setProblem(t('w21.permits.needSign'));
    if (name.trim() === '') return setProblem(t('w21.permits.needName'));
    setProblem(null);
    accept.mutate();
  };
  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-medium">{t('w21.permits.heading', { type: permit.typeName, number: permit.number })}</h3>
        {permit.accepted ? <Badge tone="good">{t('w21.permits.acceptedBadge')}</Badge> : <Badge tone="warn">{t(`w21.permits.status.${permit.status}`)}</Badge>}
      </div>
      <dl className="space-y-1 text-sm">
        {permit.area ? <div><dt className="inline font-medium">{t('w21.permits.area')}: </dt><dd className="inline">{permit.area}</dd></div> : null}
        {permit.startsAt || permit.endsAt ? <div><dt className="inline font-medium">{t('w21.permits.when')}: </dt><dd className="inline">{t('w21.permits.fromTo', { from: when(permit.startsAt), to: when(permit.endsAt) })}</dd></div> : null}
      </dl>
      {permit.conditions ? <div><h4 className="text-sm font-medium">{t('w21.permits.conditions')}</h4><p className="whitespace-pre-line text-sm text-slate-800">{permit.conditions}</p></div> : null}
      {permit.precautions ? <div><h4 className="text-sm font-medium">{t('w21.permits.precautions')}</h4><p className="whitespace-pre-line text-sm text-slate-800">{permit.precautions}</p></div> : null}
      {canAccept ? (
        <>
          <p className="text-sm text-slate-700">{t('w21.permits.acceptHelp')}</p>
          <TextField label={t('w21.permits.name')} value={name} maxLength={120} autoComplete="name" onChange={(e) => setName(e.target.value)} />
          <SignaturePad label={t('w21.permits.signHere')} disabled={accept.isPending} onChange={setSig} />
          {problem ? <Alert>{problem}</Alert> : null}
          <Button variant="primary" className="min-h-12 w-full" disabled={accept.isPending} onClick={submit}>
            {t('w21.permits.accept')}
          </Button>
        </>
      ) : null}
    </div>
  );
}

export function PermitsSection({ token }: { token: string }) {
  const qc = useQueryClient();
  const key = ['public-link', 'prearrival', token, 'permits'];
  const q = useQuery({ queryKey: key, queryFn: () => api.get(`/permits/prearrival/${enc(token)}`, { schema: PrearrivalPermits, ...quiet }), retry: false });
  if (!q.data || q.data.permits.length === 0) return null;
  return (
    <Section title={t('w21.permits.title')}>
      <p className="text-sm text-slate-700">{t('w21.permits.help')}</p>
      {q.data.permits.map((p) => (
        <PermitCard key={p.id} token={token} permit={p} onAccepted={(v) => qc.setQueryData(key, v)} />
      ))}
    </Section>
  );
}

/** The three Phase 3 sections, drawn under the Phase 2 ones on the open pre-arrival page. */
export function PreArrivalExtras({ token }: { token: string }) {
  return (
    <>
      <DocumentsSection token={token} />
      <InductionSection token={token} />
      <PermitsSection token={token} />
    </>
  );
}
